<script lang="ts">
    import $ from "jquery";
    import Vue from "vue";
    import { Component } from 'vue-facing-decorator';
    import { KendoChart, KendoChartDataSerie, KendoChartOptions } from "@Kendo/Components/KendoChart";
    import { getDistinctColor, numCompare, roundDecimal, splitString } from "@common/Common";
    import { WeightsViewerCriterionReadModel, WeightsViewerReadModel } from "@Criteria/Weights/Domain/readmodels";

    
    @Component({
        props: {
            propOpened: { type: Boolean, required: true, default: false },
            propRm: { type: Object, required: false, default: null }
        },
        watch: {
            selectedCriterion: 'onSelectedCriterionChanged',
            precision: 'onPrecisionChanged',
            dataVizType: 'onDataVizTypeChanged'
        }
    })
    export default class WeightsViewerDialog extends Vue
    {
        get opened(): boolean { return this.$props.propOpened; }
        get rm(): WeightsViewerReadModel { return this.$props.propRm as WeightsViewerReadModel; }
        
        PieChart: KendoChart | null = null;
        BarChart: KendoChart | null = null;

        get selectedCriterionId(): number { return this.rm?.SelectedCriterionId; }
        get selectedCriterion(): WeightsViewerCriterionReadModel { return this.rm?.Criteria.find(a => a.Id === this.selectedCriterionId) ?? null; }
        get dataVizType(): string { return this.rm?.DataVizType ?? null;}
        get precision(): number { return this.rm?.Precision ?? null;}
        get precisionLabel(): string  { return `${1 / Math.pow(10, this.precision)}%`; }
        
        get Weights(): WeightsViewerCriterionReadModel[] { return this.rm?.Criteria ?? []; }
        get Parents(): WeightsViewerCriterionReadModel[] { return this.Weights.filter(a => a.IsParent === true); }
        get Subcriteria(): WeightsViewerCriterionReadModel[]  { return this.Weights.filter(a => a.ParentId === this.selectedCriterionId); }
        get Bottom(): WeightsViewerCriterionReadModel[] { return GetChildlessDescendantCriteria(this.selectedCriterionId, this.Weights); }

        //@Watch('selectedCriterion')
        onSelectedCriterionChanged(val: WeightsViewerCriterionReadModel, oldVal: WeightsViewerCriterionReadModel)
        {
            if (val != null)
            {
                this.$nextTick(() =>
                {
                    this.RenderDataViz();
                });
            }
        }

        //@Watch('precision')
        onPrecisionChanged(val: number, oldVal: number)
        {
            if (oldVal != null)
            {
                this.RenderDataViz();
            }
        }

        //@Watch('dataVizType')
        onDataVizTypeChanged(val: string, oldVal: string | null)
        {
            if (oldVal != null)
            {
                this.$nextTick(() =>
                {
                    this.RenderDataViz();
                });
            }
        }

        getLocalWeight(w: WeightsViewerCriterionReadModel): number
        {
            return roundDecimal(w.LocalWeight, this.precision);
        }

        getGlobalWeight(w: WeightsViewerCriterionReadModel): number
        {
            return roundDecimal(w.GlobalWeight, this.precision);
        }

        getLocalWeightLabel(w: WeightsViewerCriterionReadModel): string
        {
            let str = `${w.Name} (${this.getLocalWeight(w)}%)`;
            str = splitString(str, 30).join("\n");
            return str;
        }

        getGlobalWeightLabel(w: WeightsViewerCriterionReadModel): string 
        {
            let str = `${w.Name} (${this.getGlobalWeight(w)}%)`;
            str = splitString(str, 30).join("\n");
            return str;
        }

        getCriterionNameMultiline(w: WeightsViewerCriterionReadModel): string 
        {
            let str = w.Name;
            str = splitString(str, 40).join("\n");
            return str;
        }

        
        
        SetCriterion(criterionId: number)
        {
            this.rm.SelectedCriterionId = criterionId;
        }

        SetPrecision(precision: number)
        {
            this.rm.Precision = precision;
        }
        
        SwitchToChart()
        {
            this.rm.DataVizType = "Chart";
        }

        SwitchToGrid()
        {
            this.rm.DataVizType = "Grid";
        }


        GetGridData(contextId: number)
        {
            return this.Weights
                .filter(a => a.ParentId === contextId)
                .map(w =>
                {
                    const row: any = {};
                    row["CriterionId"] = w.Id;
                    row["Name"] = w.Name;
                    row["LocalWeight"] = this.getLocalWeight(w);
                    row["LocalWeightProgressBarValue"] = w.LocalWeightProgressBarValue;
                    row["GlobalWeight"] = this.getGlobalWeight(w)                                   
                    row["GlobalWeightProgressBarValue"] = w.GlobalWeightProgressBarValue;
                    row["HasChildren"] = w.IsParent;
                    return row;
                });
        }

        RenderGrid()
        {
            const data = this.GetGridData(this.selectedCriterionId);

            let grid = $('#grid').data("kendoGrid");

            if (grid)
            {
                grid.destroy();
            }

            const element = $("#grid").kendoGrid({
                dataSource: {
                    data: data,
                    schema:
                    {
                        model:
                        {
                            fields:
                            {
                                Name: { type: "string" },
                                LocalWeight: { type: "number" },
                                GlobalWeight: { type: "number" },
                            }
                        }
                    }
                },

                height: 600,
                detailInit: (e) => { GridDetailInit(e, this) },
                dataBound: GridDataBound,
                resizable: true,
                sortable: true,
                columnMenu: true,
                filterable: true,
                columns: [
                    {
                        width: "220px",
                        field: "Name",
                        title: "Criterion",
                    },
                    {
                        field: "LocalWeight",
                        title: "Local Weight",
                        width: "220px",
                        template: GetLocalWeightColumnTemplate
                    },
                    {
                        field: "GlobalWeight",
                        title: "Global Weight",
                        width: "220px",
                        template: GetGlobalWeightColumnTemplate
                    },

                ]
            });
        }

        RenderPieChart()
        {
            const data = this.Subcriteria.map((w, i) =>
            {
                return {
                    CriterionId: w.Id,
                    Name: w.Name,
                    Weight: this.getLocalWeight(w),
                    Label: this.getLocalWeightLabel(w),
                    Color: getDistinctColor(i)
                };
            });

            const options = new KendoChartOptions();
            options.SetTooltip(true, "#= category #: #= value #%");
            options.HideLegend();
            options.SetDonutSeriesType();

            const serie = new KendoChartDataSerie();
            serie.SetField("Weight");
            serie.SetCategoryField("Name");
            serie.SetColorField("Color");
            serie.SetLabels(true, "#= dataItem.Label #", '1.2rem Arial', "outsideEnd"); //background: "transparent" 
            serie.SetLabelsAlign("circle");
            serie.EnableAutoFit();
            serie.SetData(data);

            options.SetOnSeriesClick((e) =>
            {
                const hasChildren = this.Weights.some(a => a.ParentId === e.dataItem.CriterionId)

                if (hasChildren)
                {
                    this.SetCriterion(e.dataItem.CriterionId);
                }
            });

            //if (this.PieChart)
            //{
            //    this.PieChart.Destroy();
            //}

            const chart = KendoChart.CreateChart(options);
            chart.SetDataSeries([serie]);
            this.PieChart = chart;

            this.PieChart.Render1('PieChart');
        }

        RenderBarChart()
        {
            const data = this.Subcriteria
                .map((w, i) =>
                {
                    const bottom = GetChildlessDescendantCriteria(w.Id, this.Weights);

                    if (bottom.length === 0)
                    {
                        bottom.push(w);
                    }

                    const items = bottom.map(b =>
                    {
                        return {
                            CriterionId: b.Id,
                            Name: b.Name,
                            MultilineName: this.getCriterionNameMultiline(b),
                            Weight: this.getGlobalWeight(b),
                            Label: this.getGlobalWeightLabel(b),
                            Color: getDistinctColor(i)
                        };
                    });

                    return items;
                })
                .reduce((a, b) => a.concat(b))
                .sort((a, b) => numCompare(a.Weight, b.Weight))
                .reverse();

            const options = new KendoChartOptions();
            options.SetTooltip(true, "#= dataItem.Name #: #= value #%");
            options.HideLegend();
            options.SetBarSeriesType();
            options.SetCategoryAxis(true, true, '1rem Arial');

            const serie = new KendoChartDataSerie();
            serie.SetField("Weight");
            serie.SetCategoryField("MultilineName");
            serie.SetColorField("Color");

            //serie.SetLabels(true, "#= dataItem.MultilineName #", '1.2rem Arial', null); //background: "transparent" 

            serie.SetData(data);

            //if (this.BarChart)
            //{
            //    this.BarChart.Destroy();
            //}

            const chart = KendoChart.CreateChart(options);
            chart.SetDataSeries([serie]);
            this.BarChart = chart;

            const minHeightBars = data.length * 100;
            const minVhPxl = 300;
            const height = Math.max(minHeightBars, minVhPxl);
            this.BarChart.SetHeight(height);

            this.BarChart.Render1('BarChart');
        }

        RenderDataViz()
        {
            if (this.dataVizType === "Chart")
            {
                this.RenderPieChart();
                this.RenderBarChart();
            }
            else if (this.dataVizType === "Grid")
            {
                this.RenderGrid();
            }
        }



        exportWeights()
        {
            if (this.rm.WeightsSetId)
            {
                this.$emit('export-weightsset-weights', this.rm.WeightsSetId);
            }
            else if (this.rm.SurveyId)
            {
                this.$emit('export-survey-weights', this.rm.SurveyId);
            }
            else if (this.rm.ProjectUserId)
            {
                this.$emit('export-panelist-weights', this.rm.ProjectUserId);
            }
        }

        close()
        {
            this.$emit('close-dialog');
        }
    }

    function GetChildlessDescendantCriteria(criterionId: number, allCriteria: WeightsViewerCriterionReadModel[]): WeightsViewerCriterionReadModel[]
    {
        const descendants = GetDescendantCriteria(criterionId, allCriteria);
        return descendants.filter(a => a.IsParent === false);
    }

    function GetDescendantCriteria(criterionId: number, allCriteria: WeightsViewerCriterionReadModel[]): WeightsViewerCriterionReadModel[]
    {
        const descendants: WeightsViewerCriterionReadModel[] = [];

        const subscriteria = allCriteria.filter(a => a.ParentId === criterionId);

        if (subscriteria.length > 0)
        {
            descendants.push(...subscriteria);

            subscriteria.forEach(sub =>
            {
                descendants.push(...GetDescendantCriteria(sub.Id, allCriteria));
            });
        }

        return descendants;
    }


    function GridDetailInit(e: any, parent: WeightsViewerDialog)
    {
        const data = parent.GetGridData(e.data.CriterionId);

        $("<div/>").appendTo(e.detailCell).kendoGrid({
            dataSource: {
                data: data,
                schema:
                {
                    model:
                    {
                        fields:
                        {
                            Name: { type: "string" },
                            LocalWeight: { type: "number" },
                            GlobalWeight: { type: "number" },
                        }
                    }
                }
            },
            detailInit: (e) => { GridDetailInit(e, parent) },
            dataBound: GridDataBound,
            resizable: true,
            sortable: true,
            columnMenu: true,
            filterable: true,
            columns: [
                { field: "Name", title: "Criterion" },
                { field: "LocalWeight", title: "Local Weight", width: "200px", template: GetLocalWeightColumnTemplate },
                { field: "GlobalWeight", title: "Global Weight", width: "200px", template: GetGlobalWeightColumnTemplate },
            ]
        });
    }

    function GridDataBound()
    {
        const data = this.dataSource.data();

        for (let row of data)
        {
            if (row.get("HasChildren") == false)
            {
                $('tr[data-uid="' + row.uid + '"] ').find(".k-hierarchy-cell a").hide();
            }
        }
    }

    function GetLocalWeightColumnTemplate(e: any): string
    {
        const bgColorCss = "";

        const value: number = e["LocalWeight"];
        const progress: number = e["LocalWeightProgressBarValue"];
        
        const textColorCss = progress < 10 ? 'text-dark' : '';

        const template = `<div class="progress" style="height: 26px;" title="${value}"><div class="progress-bar ${bgColorCss} ${textColorCss}" role="progressbar" style="width: ${progress}%;">${value}</div></div>`;
        return template;
    }

    function GetGlobalWeightColumnTemplate(e: any): string
    {
        const bgColorCss = "";

        const value: number = e["GlobalWeight"];
        const progress: number = e["GlobalWeightProgressBarValue"];

        const textColorCss = progress < 10 ? 'text-dark' : '';

        const template = `<div class="progress" style="height: 26px;" title="${value}"><div class="progress-bar ${bgColorCss} ${textColorCss}" role="progressbar" style="width: ${progress}%;">${value}</div></div>`;
        return template;
    }

</script>

<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-dialog-scrollable" style="max-width: 96%;">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Weights: {{rm?.Name}}</h5>
                        <button @click="close" type="button" class="close">&times;</button>
                    </div>

                    <div class="modal-body">
                        <h2 v-if="!rm"><i class="fa fa-spinner fa-spin"></i> Loading...</h2>

                        <div v-if="rm">
                            <div class="d-flex justify-content-between">
                                <div class="dropdown">
                                    <button class="btn btn-outline-secondary dropdown-toggle" data-toggle="dropdown">
                                        <span>{{selectedCriterion?.Name}}</span>
                                    </button>
                                    <div class="dropdown-menu" style="max-height: 300px; overflow-y:auto;">
                                        <a class="dropdown-item" v-for="c in Parents" :key="c.Id" @click="SetCriterion(c.Id)">
                                            <span>{{c.Name}}</span><br />
                                            <small>{{c.Path}}</small>
                                        </a>
                                    </div>
                                </div>
                                <div>
                                    <div class="dropdown d-inline-block">
                                        <button class="btn btn-outline-secondary dropdown-toggle" data-toggle="dropdown">{{precisionLabel}}</button>
                                        <div class="dropdown-menu">
                                            <h6 class="dropdown-header">Precision:</h6>
                                            <a class="dropdown-item" @click="SetPrecision(0)">1</a>
                                            <a class="dropdown-item" @click="SetPrecision(1)">0.1</a>
                                            <a class="dropdown-item" @click="SetPrecision(2)">0.01</a>
                                            <a class="dropdown-item" @click="SetPrecision(3)">0.001</a>
                                            <a class="dropdown-item" @click="SetPrecision(4)">0.0001</a>
                                            <a class="dropdown-item" @click="SetPrecision(5)">0.00001</a>
                                        </div>
                                    </div>

                                    <div class="btn-group" role="group">
                                        <button type="button" class="btn btn-outline-secondary" @click="SwitchToChart">
                                            <i class="fa fa-pie-chart" aria-hidden="true"></i>
                                        </button>
                                        <button type="button" class="btn btn-outline-secondary" @click="SwitchToGrid">
                                            <i class="fa fa-table" aria-hidden="true"></i>
                                        </button>
                                    </div>

                                    <button type="button" class="btn btn-outline-secondary" @click="exportWeights">
                                        <i class="fa fa-download fa-lg"></i>
                                    </button>
                                </div>
                            </div>

                            <br />
                            <div v-show="dataVizType === 'Chart'">
                                <div id="Chart-PieChart"></div>
                                <div id="Chart-BarChart"></div>
                            </div>
                            <div v-show="dataVizType === 'Grid'">
                                <div id="grid"></div>
                            </div>
                            <br />
                        </div>
                    </div>

                    <div class="modal-footer">
                        <button @click="close" class="btn btn-outline-secondary">Close</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style>

</style>